import {
  BrowserCacheLocation,
  PublicClientApplication,
  type EndSessionRequest,
  type IdTokenClaims,
  type RedirectRequest,
  type SilentRequest,
} from '@azure/msal-browser';

const flow = 'B2C_1_SignIn';

const msal = new PublicClientApplication({
  auth: {
    clientId: import.meta.env.VITE_OAUTH_CLIENT_ID,
    authority: `https://${import.meta.env.VITE_OAUTH_SUBDOMAIN}.b2clogin.com/${import.meta.env.VITE_OAUTH_SUBDOMAIN}.onmicrosoft.com/${flow}`,
    knownAuthorities: [
      `${import.meta.env.VITE_OAUTH_SUBDOMAIN}.b2clogin.com`,
      `${import.meta.env.VITE_OAUTH_SUBDOMAIN}.onmicrosoft.com`,
    ],
    redirectUri: `${window.location.origin}/login/oauth2/code`,
  },
  cache: {
    cacheLocation: BrowserCacheLocation.LocalStorage,
  },
});
const scopes: string[] = [import.meta.env.VITE_OAUTH_CLIENT_ID];

export const useAuth = () => {
  const loginRedirect = async (): Promise<void> => {
    const redirectRequest: RedirectRequest = {
      account: getAccountInfo(),
      scopes,
    };
    await msal.loginRedirect(redirectRequest);
  };

  const logoutRedirect = async (): Promise<void> => {
    const redirectRequest: EndSessionRequest = {
      account: getAccountInfo(),
      postLogoutRedirectUri: window.location.origin,
    };
    await msal.logoutRedirect(redirectRequest);
  };

  const isUserSignedIn = async (): Promise<boolean> => {
    try {
      const response = await msal.handleRedirectPromise();
      if (response?.account) {
        msal.setActiveAccount(response.account);
        return true;
      }
      const account = msal.getAllAccounts().at(0);
      if (account) {
        msal.setActiveAccount(account);
        return true;
      }

      return false;
    } catch {
      return false;
    }
  };

  const protect = async (): Promise<void> => {
    const signedIn = await isUserSignedIn();
    if (!signedIn) {
      await loginRedirect();
    }
  };

  const init = () => {
    return msal.initialize();
  };

  const getToken = async (): Promise<string> => {
    const request: SilentRequest = {
      account: getAccountInfo(),
      scopes,
    };
    try {
      const res = await msal.handleRedirectPromise();
      if (res) {
        msal.setActiveAccount(res.account);
        return ensureTokenValidity(res.idToken);
      }
      const response = await msal.acquireTokenSilent(request);
      msal.setActiveAccount(response.account);
      return ensureTokenValidity(response.idToken);
    } catch (error) {
      await msal.acquireTokenRedirect(request);
      throw error;
    }
  };

  const getAccountInfo = () => {
    const account = msal.getActiveAccount();
    return account ?? undefined;
  };

  const isTokenExpired = (token: string) => {
    try {
      const encodedPayloadString = token.split('.').at(1);
      if (encodedPayloadString === undefined) {
        return true;
      }
      const decodedPayloadString = atob(encodedPayloadString);
      const payload: IdTokenClaims = JSON.parse(decodedPayloadString);
      const expired = payload.exp;
      return expired !== undefined && expired * 1000 <= Date.now();
    } catch {
      return true;
    }
  };

  /** Checks if the token is expired and redirects to login page if necessary */
  const ensureTokenValidity = async (token: string): Promise<string> => {
    if (isTokenExpired(token)) {
      await msal.acquireTokenRedirect({ scopes });
    }
    return token;
  };

  return { getToken, getAccountInfo, init, isUserSignedIn, protect, logoutRedirect };
};
